import { type ApolloCache, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

import {
  gql,
  NateraIntakeResumeDestination,
} from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../lib/apollo/utils";

export const pendingNateraIntakeQuery = gql(/* GraphQL */ `
  query PendingNateraIntake {
    hasPendingNateraIntake
  }
`);

const PENDING_READ_CAP_MS = 3000;

/**
 * Whether the member is holding funnel answers no case has been opened from —
 * the one thing the resume gate needs, read on its own rather than on the boot
 * profile: an API that does not serve the field yet, or a read that fails,
 * costs the redirect and nothing else. `undefined` until it lands; false if
 * the read fails or outlasts the cap.
 */
export function usePendingNateraIntake({ skip }: { skip: boolean }) {
  const { data, error } = useQuery(pendingNateraIntakeQuery, {
    skip,
    context: errorHandling("caller"),
  });
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (skip) {
      return;
    }
    const timer = setTimeout(() => setTimedOut(true), PENDING_READ_CAP_MS);
    return () => clearTimeout(timer);
  }, [skip]);
  const answer = data?.hasPendingNateraIntake;
  if (answer !== undefined) {
    return answer;
  }
  return error || timedOut ? false : undefined;
}

export function primePendingNateraIntake(
  cache: ApolloCache<unknown>,
  hasPendingNateraIntake: boolean,
) {
  cache.writeQuery({
    query: pendingNateraIntakeQuery,
    data: { __typename: "Query", hasPendingNateraIntake },
  });
}

export const nateraIntakeResumeQuery = gql(/* GraphQL */ `
  query NateraIntakeResume {
    nateraIntakeResumeDestination
  }
`);

export type NateraIntakeResume = "activation" | "home";

/**
 * Where a signed-in member belongs instead of the funnel: activation when it has
 * something to open or resume, home otherwise, so nobody reaches activation
 * with nothing to build a case from. `undefined` until the read lands; a
 * failed read answers activation, whose own failure screen still offers home.
 */
export function useNateraIntakeResume({
  skip,
}: {
  skip: boolean;
}): NateraIntakeResume | undefined {
  const { data, error } = useQuery(nateraIntakeResumeQuery, {
    skip,
    // A one-shot decision, kept out of the cache so later cache writes cannot
    // re-answer it mid-activation.
    fetchPolicy: "no-cache",
    context: errorHandling("caller"),
  });
  if (error) {
    return "activation";
  }
  if (!data) {
    return undefined;
  }
  return data.nateraIntakeResumeDestination ===
    NateraIntakeResumeDestination.HOME
    ? "home"
    : "activation";
}

/**
 * Activation owns the answer from the moment it acts on the packet: the server
 * clears it when the case opens, and a member who gives up on a failure has to
 * be able to leave even while it is still there. Either way the gate stops for
 * this session, and the next launch reads the server again.
 *
 * It is not the only thing holding the trap shut:
 * `AuthService.hasPendingNateraIntake` answers false once the member owns a
 * Natera case at all — narrowing that predicate back to the packet alone
 * reopens the loop.
 */
export function stopGatingActivation(cache: ApolloCache<unknown>) {
  primePendingNateraIntake(cache, false);
}
